/**
 * Botón del sistema. Cuatro variantes y nada más: primario teal, secundario ghost,
 * peligro y sutil. El estado `loading` bloquea el click — una acción sensible que se
 * dispara dos veces es un cobro duplicado.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "../../lib/cn.js";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  block?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-white border border-accent hover:bg-accent-deep hover:border-accent-deep",
  secondary: "bg-panel text-ink border border-line hover:bg-panel-2 hover:border-line-strong",
  danger: "bg-danger/15 text-danger-fg border border-danger/40 hover:bg-danger hover:text-white",
  ghost: "bg-transparent text-dim border border-transparent hover:text-ink hover:bg-fill-2",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-[12px] gap-1.5 rounded-[8px]",
  md: "h-9 px-4 text-[13px] gap-2 rounded-[10px]",
  lg: "h-11 px-5 text-[14px] gap-2 rounded-[10px]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    loading = false,
    icon,
    iconRight,
    block = false,
    className,
    disabled,
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center font-medium transition-colors select-none",
        "focus-visible:outline-none disabled:opacity-45 disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 aria-hidden className="size-4 animate-[viva-spin_.8s_linear_infinite]" />
      ) : (
        icon
      )}
      {children}
      {!loading && iconRight}
    </button>
  );
});
